import styled from "styled-components";
import React, { useState, useEffect } from "react";
import { Button, Checkbox, Form, Message, Modal } from "semantic-ui-react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
  create as settlementCreate,
  clearError,
} from "../../redux/modules/settlement";
import { getEstate } from "../../redux/modules/estates";
import { ChargeListSmall } from "../billing/charge-list-small";
import { CalendarInput } from "../../components/common/calendar-input";
import { FormdataManager } from "../../components/common/formdata";
import { ErrorWrap } from "../../helpers/error";
import { PaymentMethod } from "../../config";
import { t } from "../../constant/translations";
import { Currency } from "../../containers/common/currency";
import dayjs from "dayjs";
import { getAppCurrency } from "../../helpers/currency";

const AmountField = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;

  > :first-child {
    flex-grow: 1;
  }
`;

const QuickAmount = styled.div`
  margin: 1.55em 0 1em 1em;

  > button {
    font-size: 1.07em !important;
    margin-right: 0 !important;
  }
`;

export const PayAction = ({ estate, triggerText }) => {
  const [formData, setFormData] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.settlement.pending);
  const error = useSelector((state) =>
    ErrorWrap.fromError(state.settlement.error)
  );

  const resetFormData = () => {
    setFormData({
      estate: estate.id,
      settle_date: dayjs().format("YYYY-MM-DD"),
      payment_method: PaymentMethod.cash,
      deposit_amount: "",
      description: t("labels.payment"),
      auto_settle: true,
      charges: [],
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!loading) {
      dispatch(settlementCreate(formData)).then((resp) => {
        if (!resp.error) {
          dispatch(getEstate(formData.estate));
          setModalOpen(false);
        }
      });
    }
  };

  useEffect(() => {
    resetFormData();
  }, [estate.id]);

  const form = new FormdataManager(formData, setFormData);
  form.withError(error);
  const paymentOptions = [
    {
      text: t(`paymentMethods.${PaymentMethod.cash}`),
      value: PaymentMethod.cash,
    },
    {
      text: t(`paymentMethods.${PaymentMethod.bank}`),
      value: PaymentMethod.bank,
    },
  ];

  return (
    <div>
      <Modal
        onOpen={() => setModalOpen(true)}
        onClose={() => {
          if (!loading) {
            setModalOpen(false);
            dispatch(clearError());
          }
        }}
        open={modalOpen}
        trigger={
          <Button
            positive
            basic
            size={"tiny"}
            icon={"payment"}
            content={triggerText}
            title={t("estate.pay")}
          />
        }
        closeIcon={true}
      >
        <Modal.Header>
          {t("estate.new_payment_to")} {estate.name}
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <Form onSubmit={handleSubmit} error={error.exists()}>
              <AmountField>
                <Form.Field
                  control="input"
                  name="deposit_amount"
                  label={t("labels.amount")}
                  type="number"
                  step="0.01"
                  fluid={true}
                  autoFocus="autofocus"
                  error={error.has("deposit_amount")}
                  {...form.getAttributes("deposit_amount")}
                />
                <QuickAmount>
                  <Button
                    type={"button"}
                    size={"medium"}
                    color={"green"}
                    onClick={() => form.setValue("deposit_amount", estate.debt)}
                    content={<Currency amount={estate.debt} />}
                  />
                </QuickAmount>
              </AmountField>

              <CalendarInput
                label={t("labels.date")}
                {...form.getAttributes("settle_date")}
                toMonth={dayjs().toDate()}
              />
              <Form.Field
                control="textarea"
                label={t("labels.description")}
                rows={3}
                {...form.getAttributes("description")}
              />
              <Form.Select
                label={t("settlement.payment_method")}
                rows={3}
                options={paymentOptions}
                {...form.getAttributes("payment_method")}
              />
              <Form.Field>
                <Message>
                  <Checkbox
                    toggle
                    label={t("estate.automatic_payment")}
                    checked={form.getValue("auto_settle")}
                    onChange={() =>
                      form.setValue(
                        "auto_settle",
                        !form.getValue("auto_settle")
                      )
                    }
                  />
                  <p>{t("estate.checkmark_debt_order")}</p>
                </Message>
              </Form.Field>
              <ChargeListSmall
                estate={estate}
                selectableRows={!form.getValue("auto_settle")}
                onSelect={(selected) => form.setValue("charges", [...selected])}
                amountField={"amount_left"}
              />
              <Message
                error
                content={error.exists() ? error.getGlobal() : t("error.savng")}
              />
              <Button
                type="submit"
                fluid
                size="large"
                positive
                loading={loading}
              >
                {t("estate.pay")}
              </Button>
            </Form>
          </Modal.Description>
        </Modal.Content>
      </Modal>
    </div>
  );
};

PayAction.propTypes = {
  estate: PropTypes.object.isRequired,
  triggerText: PropTypes.string,
};

export default PayAction;
